import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import ActivityFeed from "./ActivityFeed";
import AdminIcon from "./adminIcons";
import { ADMIN_BUTTON_SX, ADMIN_COLORS, ADMIN_ICON_SIZE, StatusChip, formatSlashDate } from "./adminUi";
import {
  RECORD_CHIP_SX,
  RECORD_INFO_GRID_SX,
  RECORD_TYPE,
  RecordInfoItem,
  RecordLoginSwitch,
  RecordProfileHeader,
  RecordSectionHeader,
  RecordTabs,
} from "./AdminRecordParts";
import { formatAadhaar } from "./NewJoineeDialog";
import { JOINEE_FORMS, deriveJoineeStatus, joineeStatusChipMeta } from "./joineeStatus";

var RECORD_TABS = [
  { id: "forms", label: "Forms" },
  { id: "activity", label: "Activity" },
];

var FORM_ROW_SX = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  py: "14px",
  pr: "6px",
  textAlign: "left",
  borderBottom: "1px solid " + ADMIN_COLORS.lineSoft,
};

var ACTIVE_ITEM_TYPE = Object.assign({}, RECORD_TYPE.item, { fontWeight: 700 });

function FormRowContent(props) {
  return (
    <>
      <Typography sx={Object.assign({ color: "inherit", minWidth: 0 }, props.active ? ACTIVE_ITEM_TYPE : RECORD_TYPE.item)}>
        {props.form.label}
      </Typography>
      <StatusChip status={props.status} borderless sx={RECORD_CHIP_SX} />
    </>
  );
}

/**
 * Employee Record body shared by the list drawer and the review page.
 * `selectedFormId` + `onSelectForm` turn the form rows (and form names in Activity) into a
 * selectable nav (review page); otherwise they are read-only (drawer).
 */
function EmployeeRecordPanel(props) {
  var details = props.details || {};
  var [keyInfoOpen, setKeyInfoOpen] = useState(props.keyInfoDefaultOpen !== false);
  var overall = joineeStatusChipMeta(deriveJoineeStatus(details));
  var roleLine = [details.designation, details.workLocation].filter(Boolean).join(" • ");
  var selectable = Boolean(props.onSelectForm);
  var [tab, setTab] = useState("forms");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <RecordProfileHeader
        photo={details.profilePicture || details.profilePhoto}
        name={details.name}
        subtitle={roleLine}
        email={details.email}
        chip={<StatusChip meta={overall} sx={RECORD_CHIP_SX} />}
      />

      <Box>
        <RecordSectionHeader
          title="Key Information"
          expanded={keyInfoOpen}
          onToggle={function () {
            setKeyInfoOpen(!keyInfoOpen);
          }}
        />
        <Collapse in={keyInfoOpen} timeout="auto">
          <Box sx={RECORD_INFO_GRID_SX}>
            <RecordInfoItem label="Aadhaar Number" value={formatAadhaar(details.aadhaar)} />
            <RecordInfoItem label="PAN Number" value={details.pan} />
            <RecordInfoItem label="Date of Joining" value={formatSlashDate(details.doj)} />
            <RecordInfoItem label="Login">
              <RecordLoginSwitch
                checked={props.isActive}
                onChange={props.onToggleActive}
                ariaLabel="Toggle candidate login"
              />
            </RecordInfoItem>
          </Box>
        </Collapse>
      </Box>

      <Box>
        <RecordTabs ariaLabel="Employee record" tabs={RECORD_TABS} value={tab} onChange={setTab} />
        <Box role="tabpanel" id={"record-tabpanel-" + tab} aria-labelledby={"record-tab-" + tab} sx={{ pt: "6px" }}>
        {tab === "activity" ? (
          <ActivityFeed
            candidateId={details._id}
            refreshKey={String(details.updatedAt) + "|" + Boolean(props.isActive)}
            onOpenForm={selectable ? props.onSelectForm : null}
          />
        ) : selectable ? (
          <Box component="nav" aria-label="Forms">
            {JOINEE_FORMS.map(function (form) {
              var active = props.selectedFormId === form.id;
              return (
                <ButtonBase
                  key={form.id}
                  aria-current={active ? "page" : undefined}
                  onClick={function () {
                    props.onSelectForm(form.id);
                  }}
                  sx={Object.assign({}, FORM_ROW_SX, {
                    pl: "12px",
                    pr: "10px",
                    borderLeft: "4px solid " + (active ? ADMIN_COLORS.brand : "transparent"),
                    bgcolor: active ? ADMIN_COLORS.brandSoft : "transparent",
                    color: active ? ADMIN_COLORS.brand : ADMIN_COLORS.label,
                    transition: "background-color 120ms",
                    "&:hover": { bgcolor: active ? ADMIN_COLORS.brandSoft : ADMIN_COLORS.page },
                    "&.Mui-focusVisible": { outline: "2px solid " + ADMIN_COLORS.brand, outlineOffset: "-2px" },
                  })}
                >
                  <FormRowContent form={form} status={details[form.key]} active={active} />
                </ButtonBase>
              );
            })}
          </Box>
        ) : (
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {JOINEE_FORMS.map(function (form) {
              return (
                <Box component="li" key={form.id} sx={Object.assign({}, FORM_ROW_SX, { pl: "6px", color: ADMIN_COLORS.label })}>
                  <FormRowContent form={form} status={details[form.key]} />
                </Box>
              );
            })}
          </Box>
        )}
        </Box>
      </Box>
    </Box>
  );
}

/** Download + "View All Forms" (drawer, via `onViewAll`) or Notify Email (review page), pinned at the bottom. */
export function EmployeeRecordActions(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        pt: "16px",
        borderTop: "1px solid " + ADMIN_COLORS.lineSoft,
        "& > *": { flex: "1 1 160px" },
      }}
    >
      <Button
        variant="contained"
        onClick={props.onDownload}
        disabled={props.downloading || props.disabled}
        startIcon={
          props.downloading ? (
            <CircularProgress size={ADMIN_ICON_SIZE.button} color="inherit" />
          ) : (
            <AdminIcon name="Download" size={ADMIN_ICON_SIZE.button} />
          )
        }
        sx={[ADMIN_BUTTON_SX.contained, { height: 44 }]}
      >
        {props.downloading ? "Preparing…" : "Download"}
      </Button>
      {props.onViewAll ? (
        <Button
          variant="outlined"
          onClick={props.onViewAll}
          disabled={props.disabled}
          startIcon={<AdminIcon name="ListMagnifyingGlass" size={ADMIN_ICON_SIZE.button} />}
          sx={[ADMIN_BUTTON_SX.outlined, { height: 44 }]}
        >
          View All Forms
        </Button>
      ) : (
      <Button
        variant="outlined"
        onClick={props.onNotify}
        disabled={props.notifying || props.disabled}
        startIcon={
          props.notifying ? (
            <CircularProgress size={ADMIN_ICON_SIZE.button} color="inherit" />
          ) : (
            <AdminIcon name="Bell" size={ADMIN_ICON_SIZE.button} />
          )
        }
        sx={[ADMIN_BUTTON_SX.outlined, { height: 44 }]}
      >
        {props.notifying ? "Sending…" : "Notify Email"}
      </Button>
      )}
    </Box>
  );
}

export default EmployeeRecordPanel;
